import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {animate, useMotionValue} from 'framer-motion'
import type {
  PricingSectionSlice,
  PricingSectionSliceDefaultPrimaryFirstPackageItem,
  PricingSectionSliceReskinPrimaryFirstPackageItem,
} from 'prismicio-types'
import {useEffect, useState} from 'react'
import {getFill} from 'slices/PricingSection/utils'
import {
  getModuleCardBackgroundClass,
  getModuleCardDescriptionTextClass,
  getModuleCardTitleTextClass,
} from 'slices/PricingSection/utils/cardStyles'
import {LinkWrapper} from 'src/common/LinkWrapper'
import * as paths from './IconPaths'
import {IconWrapper} from './IconWrapper'

type InteractiveModuleCardProps = (
  PricingSectionSliceDefaultPrimaryFirstPackageItem | PricingSectionSliceReskinPrimaryFirstPackageItem
) & {variation: PricingSectionSlice['variation']; iconContainer?: boolean}

export const InteractiveModuleCard = ({
  link_url,
  module_title,
  background_color,
  module_icon,
  description,
  variation,
  iconContainer = false,
}: InteractiveModuleCardProps) => {
  const [pathIndex, setPathIndex] = useState(0)
  const indexValue = useMotionValue(pathIndex)
  const isLinkFilled = isFilled.link(link_url)

  const handleHover = () => setPathIndex(1)
  const handleMouseLeave = () => setPathIndex(0)

  const path = paths[module_icon as keyof typeof paths]

  // Trigger animation on path change
  useEffect(() => {
    const animation = animate(indexValue, pathIndex, {
      duration: 0.2,
      ease: 'linear',
      delay: 0,
    })

    return () => {
      animation.stop()
    }
  }, [indexValue, pathIndex])

  return (
    <LinkWrapper
      className="group relative lg:col-span-3"
      link={link_url}
      onMouseEnter={handleHover}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(
          'relative flex flex-col gap-3 p-[22px] lg:col-span-3 lg:justify-between',
          getModuleCardBackgroundClass(background_color),
          {
            'border border-grey-135': background_color === 'Moss White' && variation !== 'reskin',
            'lg:h-[288px] rounded-xl': variation !== 'reskin',
            'lg:h-[260px] rounded-lg': variation === 'reskin',
          },
        )}
      >
        <div className="flex justify-between">
          <span
            className={cn(getModuleCardTitleTextClass(background_color), {
              'text-body-bold-l': variation !== 'reskin',
              'text-[18px] font-medium leading-[24px]': variation === 'reskin',
              'w-fit inline-block transition-all duration-500': isLinkFilled,
              'group-hover:text-moss-grey-400': background_color === 'Moss White' && variation === 'reskin',
              'group-hover:text-green-neon': background_color === 'Dark Green' && variation,
            })}
          >
            {module_title}
          </span>
          <IconWrapper hasContainer={iconContainer}>
            <svg>
              <path fillRule="evenodd" clipRule="evenodd" d={path} fill={getFill(variation, background_color)} />
            </svg>
          </IconWrapper>
        </div>
        <PrismicRichText
          field={description}
          components={{
            paragraph: ({children}) => (
              <p
                className={cn(getModuleCardDescriptionTextClass(background_color, variation), {
                  'text-body-s': variation !== 'reskin',
                  'text-[15px] leading-5': variation === 'reskin',
                })}
              >
                {children}
              </p>
            ),
          }}
        />
        {variation === 'reskin' && background_color === 'Moss White' && (
          <svg
            className="pointer-events-none absolute inset-0 size-full text-moss-grey-200"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <rect
              x="1"
              y="1"
              width="calc(100% - 1.5px)"
              height="calc(100% - 1.5px)"
              rx="8"
              ry="8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5px"
              strokeDasharray="5 5"
            />
          </svg>
        )}
        {variation === 'reskin' && background_color !== 'Moss White' && (
          <div
            className={cn('pointer-events-none absolute inset-0 rounded-lg', {
              'border border-black/[10%]': background_color === 'Neon Green',
            })}
          />
        )}
      </div>
    </LinkWrapper>
  )
}
